import { z } from 'zod';
import { PERMISSIONS, type Permission } from '../permissions';

// Backoffice MCP credential — the `adm_` bearer an operator mints so an MCP client
// can read the platform over MCP (IMM-522.1). Managed from `admin.immoos.ai`
// under `/v1/admin/mcp-access`.
//
// Deliberately NOT part of the api-key schemas: a workspace API key is bound to
// a workspace and a member, this credential is bound to neither. It is
// cross-tenant, read-only, and attributed to the operator who issued it.

export const MCP_ACCESS_NAME_MAX_LENGTH = 80;

// How long a credential authenticates for, chosen at mint. Bounded by design —
// there is no unlimited option and 90 days is the ceiling.
export const MCP_ACCESS_VALIDITY_DAYS = [7, 30, 90] as const;
export const McpAccessValidityDaysSchema = z.union([z.literal(7), z.literal(30), z.literal(90)]);
export type McpAccessValidityDays = z.infer<typeof McpAccessValidityDaysSchema>;

export const DEFAULT_MCP_ACCESS_VALIDITY_DAYS = 30 satisfies McpAccessValidityDays;

// At most this many credentials may be active per operator at once — enough for
// one per machine, low enough that an abandoned credential gets noticed.
export const MAX_ACTIVE_MCP_ACCESS_PER_ADMIN = 5;

// Derived on read, never stored: `revoked` when revoked, else `expired` when the
// expiry has passed, else `active`. A stored column would need a sweep job to
// stay true and would read `active` for an expired credential after a missed run.
export const McpAccessStatusSchema = z.enum(['active', 'revoked', 'expired']);
export type McpAccessStatus = z.infer<typeof McpAccessStatusSchema>;

/**
 * One credential as any read surface projects it.
 *
 * There is deliberately NO secret field on this type — not a nullable one, not
 * an optional one, absent. The plaintext exists only on the mint response; a
 * nullable field here would invite a read path that populates it.
 */
export const BackofficeCredentialItemSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  status: McpAccessStatusSchema,
  expiresAt: z.iso.datetime(),
  createdAt: z.iso.datetime(),
  lastUsedAt: z.iso.datetime().nullable(),
  revokedAt: z.iso.datetime().nullable(),
});
export type BackofficeCredentialItem = z.infer<typeof BackofficeCredentialItemSchema>;

export const ListMcpAccessResponseSchema = z.object({
  credentials: z.array(BackofficeCredentialItemSchema),
});
export type ListMcpAccessResponse = z.infer<typeof ListMcpAccessResponseSchema>;

/**
 * Grants an operator opts into at mint, per credential (IMM-1155). Every other entry
 * of the backend's credential catalogue is conferred whenever the issuer holds it;
 * these only when ticked. Omitting `optionalGrants` confers none of them.
 */
export const MCP_ACCESS_OPTIONAL_GRANTS = [
  'backoffice:product-skill:read',
  'backoffice:product-skill:manage',
  'backoffice:property:memory-manage',
  'backoffice:property:document-update',
] as const satisfies ReadonlyArray<Permission>;
export const McpAccessOptionalGrantSchema = z.enum(MCP_ACCESS_OPTIONAL_GRANTS);
export type McpAccessOptionalGrant = z.infer<typeof McpAccessOptionalGrantSchema>;

export function isMcpAccessOptionalGrant(permission: string): permission is McpAccessOptionalGrant {
  return (MCP_ACCESS_OPTIONAL_GRANTS as ReadonlyArray<string>).includes(permission);
}

export const IssueMcpAccessBodySchema = z.object({
  name: z.string().trim().min(1).max(MCP_ACCESS_NAME_MAX_LENGTH),
  validityDays: McpAccessValidityDaysSchema,
  optionalGrants: z.array(McpAccessOptionalGrantSchema).max(MCP_ACCESS_OPTIONAL_GRANTS.length).optional(),
});
export type IssueMcpAccessBody = z.infer<typeof IssueMcpAccessBodySchema>;

/**
 * The only response that ever carries the plaintext. `connectCommand` is the
 * ready-to-paste `claude mcp add …` line carrying the endpoint and the secret,
 * so the operator never has to assemble it by hand (and never has to paste the
 * bare secret somewhere it might be captured).
 */
export const IssueMcpAccessResponseSchema = z.object({
  credential: BackofficeCredentialItemSchema,
  secret: z.string(),
  connectCommand: z.string(),
});
export type IssueMcpAccessResponse = z.infer<typeof IssueMcpAccessResponseSchema>;

export const RevokeMcpAccessPathParamsSchema = z.object({
  id: z.uuid(),
});
export type RevokeMcpAccessPathParams = z.infer<typeof RevokeMcpAccessPathParamsSchema>;

export const RevokeMcpAccessResponseSchema = z.object({
  credential: BackofficeCredentialItemSchema,
});
export type RevokeMcpAccessResponse = z.infer<typeof RevokeMcpAccessResponseSchema>;

// Incident-response projection (IMM-522.4): the platform-wide list carries the
// issuing operator so a responder can see who holds what, and the foreign
// revoker separately from the issuer so the log stays interpretable.
export const BackofficeCredentialAdminItemSchema = BackofficeCredentialItemSchema.extend({
  issuedByAdminId: z.uuid(),
  issuedByAdminEmail: z.string().nullable(),
  revokedByAdminId: z.uuid().nullable(),
});
export type BackofficeCredentialAdminItem = z.infer<typeof BackofficeCredentialAdminItemSchema>;

export const ListAllMcpAccessResponseSchema = z.object({
  credentials: z.array(BackofficeCredentialAdminItemSchema),
});
export type ListAllMcpAccessResponse = z.infer<typeof ListAllMcpAccessResponseSchema>;

export const RevokeAnyMcpAccessResponseSchema = z.object({
  credential: BackofficeCredentialAdminItemSchema,
});
export type RevokeAnyMcpAccessResponse = z.infer<typeof RevokeAnyMcpAccessResponseSchema>;

// `changedAt` / `changedByAdminId` are null exactly when the switch has never
// been flipped — the absent-row default, which reads as enabled.
export const McpAccessSwitchResponseSchema = z.object({
  enabled: z.boolean(),
  changedAt: z.iso.datetime().nullable(),
  changedByAdminId: z.uuid().nullable(),
});
export type McpAccessSwitchResponse = z.infer<typeof McpAccessSwitchResponseSchema>;

export const SetMcpAccessSwitchBodySchema = z.object({
  enabled: z.boolean(),
});
export type SetMcpAccessSwitchBody = z.infer<typeof SetMcpAccessSwitchBodySchema>;

// The CUSTOMER MCP surface switch (IMM-601.6) — the member-facing `/v1/mcp`
// endpoint's platform-wide kill switch. Same SHAPE as the backoffice switch
// above and deliberately its own named schema rather than a re-export: the two
// surfaces are switched independently, and a shared alias would make a future
// divergence (say, a per-workspace scope) a breaking edit to both.
//
// `changedAt` / `changedByAdminId` are null exactly when the switch has never
// been flipped — the absent-row default, which reads as enabled.
export const WorkspaceMcpSwitchResponseSchema = z.object({
  enabled: z.boolean(),
  changedAt: z.iso.datetime().nullable(),
  changedByAdminId: z.uuid().nullable(),
});
export type WorkspaceMcpSwitchResponse = z.infer<typeof WorkspaceMcpSwitchResponseSchema>;

export const SetWorkspaceMcpSwitchBodySchema = z.object({
  enabled: z.boolean(),
});
export type SetWorkspaceMcpSwitchBody = z.infer<typeof SetWorkspaceMcpSwitchBodySchema>;

// The MCP tool catalogue as the operator sees it (IMM-625.2) — every composed
// workspace tool joined with its exposure row.
//
// `kind`, `sideEffectClass` and `status` mirror the registration's own tokens
// rather than a display string: the operator app upcases them and i18n supplies
// the German, so a wording change never touches the wire.
export const McpToolExposureKindSchema = z.enum(['query', 'command']);
export type McpToolExposureKind = z.infer<typeof McpToolExposureKindSchema>;

export const McpToolExposureSideEffectClassSchema = z.enum(['auto_with_audit', 'approval_required']);
export type McpToolExposureSideEffectClass = z.infer<typeof McpToolExposureSideEffectClassSchema>;

// `stale` is NOT a registration status — a registration cannot be stale, it is
// either in the catalogue or it is not. It is the listing's third case
// (IMM-625.3): an exposure row whose `toolId` the catalogue no longer contains,
// carried on the same enum so one list expresses both directions.
export const McpToolExposureStatusSchema = z.enum(['active', 'deprecated', 'stale']);
export type McpToolExposureStatus = z.infer<typeof McpToolExposureStatusSchema>;

/**
 * Whether the catalogue still knows the tool this item names — the ONE
 * membership test over the enum (BL-049).
 *
 * Written once and exported beside the enum rather than re-typed at each call
 * site, because four layers depend on it agreeing with itself: the item
 * schema's refinement, the list handler that builds a stale item, the removal
 * that refuses a catalogued one, and the operator page that renders a row with
 * no tool behind it.
 */
export function mcpToolIsCatalogued(status: McpToolExposureStatus): boolean {
  return status !== 'stale';
}

/**
 * One catalogue tool with its exposure state — or, when `status` is `stale`, an
 * exposure row whose tool has left the catalogue (IMM-625.3).
 *
 * `changedByAdminId` is a bare string, NOT a uuid: the IMM-625.1 seed attributes
 * every pre-existing row to the sentinel `system:migration:IMM-625.1`, which a
 * uuid schema would refuse to serialise. A response schema may never be stricter
 * than the state its producer can legitimately store.
 *
 * `sideEffectClass` is null exactly for a read tool — a query has nothing to
 * classify. Both provenance fields are null for a tool nobody has ruled on:
 * IMM-625.3's boot reconciliation writes a row on ARRIVAL, and an arrival has no
 * operator and no decision time.
 *
 * Every field that DESCRIBES the tool is nullable, and the refinement below
 * makes that conditional in both directions (BL-049): present when the
 * catalogue still knows the tool, `null` when it does not. A stale row must not
 * be able to carry a leftover description of the thing it says is gone — that is
 * how a row with no tool ends up rendered as an ordinary one.
 */
export const McpToolExposureItemSchema = z
  .object({
    toolId: z.string().min(1),
    name: z.string().min(1).nullable(),
    description: z.string().nullable(),
    kind: McpToolExposureKindSchema.nullable(),
    sideEffectClass: McpToolExposureSideEffectClassSchema.nullable(),
    requiredPermission: z.enum(PERMISSIONS).nullable(),
    status: McpToolExposureStatusSchema,
    exposed: z.boolean(),
    reviewed: z.boolean(),
    // When the tool first appeared in the catalogue (IMM-625.3) — the ordering
    // key for the never-reviewed list. Non-null: every row is written either by
    // the reconciliation or by a ruling, and both know the time.
    firstSeenAt: z.iso.datetime(),
    changedAt: z.iso.datetime().nullable(),
    changedByAdminId: z.string().min(1).nullable(),
  })
  .superRefine((item, ctx) => {
    const catalogued = mcpToolIsCatalogued(item.status);
    const describing = ['name', 'description', 'kind', 'requiredPermission'] as const;

    for (const field of describing) {
      const present = item[field] !== null;

      if (present !== catalogued) {
        ctx.addIssue({
          code: 'custom',
          path: [field],
          message: catalogued
            ? `${field} is required while the tool is in the catalogue`
            : `${field} must be null for a stale exposure entry`,
        });
      }
    }

    // Not in the loop above: a catalogued QUERY tool legitimately has none, so
    // presence is only constrained in the stale direction.
    if (!catalogued && item.sideEffectClass !== null) {
      ctx.addIssue({
        code: 'custom',
        path: ['sideEffectClass'],
        message: 'sideEffectClass must be null for a stale exposure entry',
      });
    }
  });
export type McpToolExposureItem = z.infer<typeof McpToolExposureItemSchema>;

/**
 * Which slice of the list to return (IMM-625.3). Server-side rather than a
 * client filter because `unreviewed` carries an ORDER — newest arrival first —
 * and the arrival time is the server's to know.
 *
 * `stale` is the one member that does not narrow the catalogue-driven list at
 * all: it selects exactly the rows the catalogue can no longer reach.
 */
export const McpToolExposureFilterSchema = z.enum(['all', 'exposed', 'unreviewed', 'writes', 'stale']);
export type McpToolExposureFilter = z.infer<typeof McpToolExposureFilterSchema>;

/** The largest page the list will serve, and the largest batch a ruling may name (IMM-769.7). */
export const MCP_TOOL_EXPOSURE_PAGE_MAX = 200;

export const ListMcpToolExposureQuerySchema = z.object({
  filter: McpToolExposureFilterSchema.optional(),
  // The tool identifier's segment before the first `.`, matched exactly
  // (IMM-769.7). Derived from the identifier rather than declared on the
  // registration, so it cannot disagree with the contract's stable key.
  module: z.string().min(1).optional(),
  // Free text over `toolId` and `name`, case-insensitive substring — the same
  // predicate the admin page ran in the browser before this moved server-side.
  q: z.string().min(1).optional(),
  limit: z.coerce.number().int().min(1).max(MCP_TOOL_EXPOSURE_PAGE_MAX).optional(),
  offset: z.coerce.number().int().min(0).optional(),
});
export type ListMcpToolExposureQuery = z.infer<typeof ListMcpToolExposureQuerySchema>;

export const McpToolModuleFacetSchema = z.object({
  module: z.string().min(1),
  total: z.number().int().min(1),
});
export type McpToolModuleFacet = z.infer<typeof McpToolModuleFacetSchema>;

/**
 * How many tools each filter would return (IMM-625 design delta).
 *
 * Every member of `McpToolExposureFilterSchema`, always present — a filter
 * whose count is absent and a filter whose count is zero are different facts,
 * and the chip has to state the second one rather than hide it. „Nur
 * ungeprüfte 0" is the answer an operator wants most.
 */
export const McpToolExposureFilterCountsSchema = z.object({
  all: z.number().int().min(0),
  exposed: z.number().int().min(0),
  unreviewed: z.number().int().min(0),
  writes: z.number().int().min(0),
  stale: z.number().int().min(0),
});
export type McpToolExposureFilterCounts = z.infer<typeof McpToolExposureFilterCountsSchema>;

export const ListMcpToolExposureResponseSchema = z.object({
  items: z.array(McpToolExposureItemSchema),
  // What each chip would return under the current `q` — NOT under the current
  // filter, which would make every chip but the active one read zero, and not
  // under `module` either, for the same reason the module facet ignores it.
  filters: McpToolExposureFilterCountsSchema,
  // How many the current `filter` + `q` + `module` match, BEFORE paging
  // (IMM-769.7). Required rather than optional: an optional total is one every
  // caller has to branch on, and there is exactly one producer.
  total: z.number().int().min(0),
  // How many of the tools matched by the current `filter` + `q` + `module` are
  // exposed, counted BEFORE paging; stale entries are never counted, because
  // nothing can reach one (IMM-832.1). Required for the reason `total` is: an
  // optional count is one every caller has to branch on, and there is exactly
  // one producer.
  exposedTotal: z.number().int().min(0),
  // Every module present under the current `filter` + `q`, computed BEFORE
  // `module` narrows — so the facet can still offer the module the operator is
  // about to switch to, rather than only the one they are already in.
  modules: z.array(McpToolModuleFacetSchema),
});
export type ListMcpToolExposureResponse = z.infer<typeof ListMcpToolExposureResponseSchema>;

/**
 * A ruling over many tools at once (IMM-769.7).
 *
 * The bound matches the list's page ceiling: the largest batch an operator can
 * assemble is the page in front of them.
 */
export const SetMcpToolExposureBatchBodySchema = z.object({
  toolIds: z.array(z.string().min(1)).min(1).max(MCP_TOOL_EXPOSURE_PAGE_MAX),
  exposed: z.boolean(),
});
export type SetMcpToolExposureBatchBody = z.infer<typeof SetMcpToolExposureBatchBodySchema>;

export const SetMcpToolExposureBatchResponseSchema = z.object({
  items: z.array(McpToolExposureItemSchema),
});
export type SetMcpToolExposureBatchResponse = z.infer<typeof SetMcpToolExposureBatchResponseSchema>;

export const SetMcpToolExposureBodySchema = z.object({
  exposed: z.boolean(),
});
export type SetMcpToolExposureBody = z.infer<typeof SetMcpToolExposureBodySchema>;

export const SetMcpToolExposureParamsSchema = z.object({
  toolId: z.string().min(1),
});
export type SetMcpToolExposureParams = z.infer<typeof SetMcpToolExposureParamsSchema>;

/**
 * Which credential kind the reachability diagnosis is asked about (IMM-625.4).
 *
 * Exactly the two kinds that can speak the CUSTOMER MCP surface: an `immo_` API
 * key and an OAuth grant. A backoffice `adm_` credential is deliberately absent
 * — it has no bound member and no workspace, so none of the three layers the
 * diagnosis names can be evaluated for one.
 */
export const McpCredentialKindSchema = z.enum(['api-key', 'oauth-grant']);
export type McpCredentialKind = z.infer<typeof McpCredentialKindSchema>;

/**
 * The layer that keeps a tool out of a credential's reach (IMM-625.4), ordered
 * exactly as the live surface refuses: the kill switch, then the platform's
 * exposure, then the credential's own grants, then the bound member's live
 * permissions. The FIRST failure wins, so this names the layer the client
 * actually hits rather than every layer that happens to exclude it — reporting
 * all of them would send an operator to fix a grant on a tool the platform is
 * not exposing anyway.
 *
 * `SURFACE_DISABLED` is its own member rather than folded into `NOT_EXPOSED`
 * because the two call for opposite actions ("re-enable MCP" vs "expose this
 * tool"), and an operator mid-incident should not have to infer which.
 *
 * `CREDENTIAL_NOT_MCP` is the same argument one layer down: an `immo_` key that
 * was not issued for MCP holds no transport permission, so the live surface
 * refuses it at the door for EVERY tool. Folding it into `CREDENTIAL_GRANTS`
 * would name a permission (`workspace:identity:mcp-invoke`) that no member can
 * ever be granted and no operator can act on, and reporting reachable would make
 * the panel disagree with the surface it exists to explain. The action here is
 * the customer's: re-issue the key with the MCP capability ticked.
 */
export const McpToolReachabilityExclusionSchema = z.enum([
  'SURFACE_DISABLED',
  'NOT_EXPOSED',
  'CREDENTIAL_NOT_MCP',
  'CREDENTIAL_GRANTS',
  'MEMBER_PERMISSIONS',
]);
export type McpToolReachabilityExclusion = z.infer<typeof McpToolReachabilityExclusionSchema>;

/**
 * One seat an OAuth grant's bound user holds (IMM-625.4) — the picker's option,
 * and what a `identity.mcp-surface.seat-required` refusal carries in `details`
 * so the panel needs no second call.
 *
 * Mirrors the live path's `WorkspaceChoice` exactly, and for the same reason:
 * the seats offered here must be the seats the surface would honour, which are
 * resolved from the bound USER's active member rows, never from the grant's own
 * `member_id` (that records only where consent was given).
 */
export const McpCredentialSeatSchema = z.object({
  workspaceId: z.uuid(),
  workspaceName: z.string(),
  memberId: z.uuid(),
});
export type McpCredentialSeat = z.infer<typeof McpCredentialSeatSchema>;

export const DiagnoseMcpToolReachabilityParamsSchema = z.object({
  toolId: z.string().min(1),
});
export type DiagnoseMcpToolReachabilityParams = z.infer<typeof DiagnoseMcpToolReachabilityParamsSchema>;

export const DiagnoseMcpToolReachabilityQuerySchema = z.object({
  credentialKind: McpCredentialKindSchema,
  credentialId: z.uuid(),
  /**
   * The seat to answer for. Required only when the named OAuth grant's bound
   * user holds several — an API key has exactly one seat by construction, and a
   * single-seat grant needs no choice. Omitting it on a multi-seat grant is a
   * 422 carrying the candidates, never a guess.
   */
  memberId: z.uuid().optional(),
});
export type DiagnoseMcpToolReachabilityQuery = z.infer<typeof DiagnoseMcpToolReachabilityQuerySchema>;

/**
 * Whether one credential can reach one tool right now, and if not, why
 * (IMM-625.4).
 *
 * `excludedBy` is null EXACTLY when `reachable` is true — "could not compute"
 * is never representable as a decided value (BL-093), because the one case that
 * cannot be decided (an ambiguous seat) is a refusal rather than an answer.
 *
 * `requiredPermission` is returned whatever the verdict, so the copy can name
 * the permission in the grant and member refusals without a second lookup.
 *
 * `seat` is null for the two credential-independent layers, which are evaluated
 * BEFORE the credential is read — during an incident „is the whole surface off?"
 * must answer even for a credential that has since been revoked.
 */
export const McpToolReachabilityResponseSchema = z.object({
  reachable: z.boolean(),
  excludedBy: McpToolReachabilityExclusionSchema.nullable(),
  requiredPermission: z.enum(PERMISSIONS),
  seat: z
    .object({
      workspaceId: z.uuid(),
      memberId: z.uuid(),
    })
    .nullable(),
});
export type McpToolReachabilityResponse = z.infer<typeof McpToolReachabilityResponseSchema>;
